import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import {
  Plus, LogIn, LogOut, Download, MapPin,
  AlertOctagon, PlayCircle, CheckCircle2,
} from "lucide-react";
import TimePicker24 from "../components/TimePicker24";
import { toast } from "sonner";
import PointageRetardTab from "../components/pointage/PointageRetardTab";
import PointageEnCoursTab from "../components/pointage/PointageEnCoursTab";
import PointageTermineTab from "../components/pointage/PointageTermineTab";
import StickyPageHeader from "../components/StickyPageHeader";

// ---------------------------------------------------------------------------
// Page Pointage — refonte en 3 onglets ("En retard", "En cours d'exécution",
// "Terminés"). Présentation calquée sur EquipmentRequestsTab.jsx.
// IMPORTANT : le tableau de bord n'est PAS modifié — seule cette page change.
// ---------------------------------------------------------------------------

function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

// Capture GPS (haute précision) + reverse-geocoding Nominatim.
async function captureGeoLocation() {
  if (!("geolocation" in navigator)) {
    return { timezone: browserTimezone() };
  }
  const position = await new Promise((resolve) => {
    let done = false;
    const finish = (pos) => { if (!done) { done = true; resolve(pos); } };
    navigator.geolocation.getCurrentPosition(
      (pos) => finish(pos),
      () => finish(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
    setTimeout(() => finish(null), 9000);
  });

  const tz = browserTimezone();
  if (!position) return { timezone: tz };

  const { latitude, longitude, accuracy } = position.coords;
  let address = null;
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=18&accept-language=fr`,
      { headers: { Accept: "application/json" } }
    );
    if (r.ok) {
      const j = await r.json();
      address = j.display_name || null;
    }
  } catch {
    /* on garde au moins les coordonnées brutes */
  }
  return { latitude, longitude, accuracy, timezone: tz, address };
}

const TABS = [
  {
    key: "retard",
    label: "En retard",
    icon: AlertOctagon,
    cls: "border-red-200 text-red-800 bg-red-50",
    activeCls: "bg-red-600 text-white border-red-700",
  },
  {
    key: "encours",
    label: "En cours d'exécution",
    icon: PlayCircle,
    cls: "border-blue-200 text-blue-800 bg-blue-50",
    activeCls: "bg-blue-600 text-white border-blue-700",
  },
  {
    key: "termine",
    label: "Terminés",
    icon: CheckCircle2,
    cls: "border-emerald-200 text-emerald-800 bg-emerald-50",
    activeCls: "bg-emerald-600 text-white border-emerald-700",
  },
];

export default function Pointage() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const [tab, setTab] = useState("retard");
  const [refreshSignal, setRefreshSignal] = useState(0);

  // Données partagées (collaborateurs / sites / liste pointages pour openEntry)
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [openEntry, setOpenEntry] = useState(null);

  const [open, setOpen] = useState(false);
  const [editingTime, setEditingTime] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    collaborateur_id: user?.id || "",
    brand_id: "",
    date: today(),
    clock_in: nowTime(),
    clock_out: "",
    notes: "",
  });

  const isManager = user?.role === "admin" || user?.role === "manager";
  const isAdmin = user?.role === "admin";

  const loadSharedData = async () => {
    try {
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      const [u, s, t] = await Promise.all([
        api.get("/collaborateurs", { params }),
        api.get("/sites", { params }).catch(() => ({ data: [] })),
        api.get("/timeentries", { params }),
      ]);
      setUsers(u.data || []);
      setSites(s.data || []);
      const my = (t.data || []).find((x) => x.collaborateur_id === user?.id && !x.clock_out);
      setOpenEntry(my || null);
    } catch {
      /* silencieux : les onglets gèrent leurs propres erreurs */
    }
  };

  useEffect(() => { loadSharedData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshSignal, user?.id, selectedBrandId]);
  useEffect(() => {
    if (user?.id) setForm((f) => ({ ...f, collaborateur_id: user.id }));
  }, [user]);

  const triggerRefresh = () => setRefreshSignal((n) => n + 1);

  const submit = async () => {
    try {
      if (!form.brand_id) {
        toast.error("Sélectionnez un département");
        return;
      }
      setBusy(true);
      if (editingTime) {
        await api.put(`/timeentries/${editingTime.id}`, {
          collaborateur_id: form.collaborateur_id,
          brand_id: form.brand_id,
          date: form.date,
          clock_in: form.clock_in,
          clock_out: form.clock_out || null,
          notes: form.notes || "",
        });
        toast.success("Pointage mis à jour");
      } else {
        const geo = await captureGeoLocation();
        await api.post("/timeentries", { ...form, ...geo });
        toast.success("Pointage enregistré");
      }
      setOpen(false);
      setEditingTime(null);
      triggerRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const openEditTime = (t) => {
    setEditingTime(t);
    setForm({
      collaborateur_id: t.collaborateur_id,
      brand_id: t.brand_id || "",
      date: t.date || today(),
      clock_in: t.clock_in || "",
      clock_out: t.clock_out || "",
      notes: t.notes || "",
    });
    setOpen(true);
  };

  const deleteTime = async (t) => {
    if (!window.confirm("Supprimer définitivement ce pointage ?")) return;
    try {
      await api.delete(`/timeentries/${t.id}`);
      toast.success("Pointage supprimé");
      triggerRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openManualEntry = () => {
    setEditingTime(null);
    setForm({
      collaborateur_id: user?.id || "",
      brand_id: "",
      date: today(),
      clock_in: nowTime(),
      clock_out: "",
      notes: "",
    });
  };

  const quickClockIn = async () => {
    if (!brands.length) {
      toast.error("Aucun département configuré");
      return;
    }
    try {
      setBusy(true);
      const geo = await captureGeoLocation();
      if (geo.latitude == null) {
        toast.info("Géo-localisation indisponible — pointage enregistré sans position");
      }
      await api.post("/timeentries", {
        collaborateur_id: user.id,
        brand_id: brands[0].id,
        date: today(),
        clock_in: nowTime(),
        clock_out: null,
        ...geo,
      });
      toast.success("Entrée enregistrée");
      triggerRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const quickClockOut = async () => {
    if (!openEntry) return;
    try {
      setBusy(true);
      const geo = await captureGeoLocation();
      await api.put(`/timeentries/${openEntry.id}/clock_out`, null, {
        params: {
          clock_out: nowTime(),
          ...(geo.latitude != null ? { latitude_out: geo.latitude } : {}),
          ...(geo.longitude != null ? { longitude_out: geo.longitude } : {}),
          ...(geo.accuracy != null ? { accuracy_out: geo.accuracy } : {}),
          ...(geo.timezone ? { timezone_out: geo.timezone } : {}),
          ...(geo.address ? { address_out: geo.address } : {}),
        },
      });
      toast.success("Sortie enregistrée");
      triggerRefresh();
    } catch {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/timeentries/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `pointages_${today()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export généré");
    } catch {
      toast.error("Erreur d'export");
    }
  };

  return (
    <div className="space-y-6" data-testid="pointage-page">
      <StickyPageHeader testid="pointage-sticky-header">
      {/* --- En-tête + actions rapides ------------------------------------- */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase mb-2">Suivi du temps</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
            Pointage
          </h1>
          <p className="text-xs text-zinc-500 mt-1 font-mono flex items-center gap-1">
            <MapPin size={11} /> Géo-localisation activée — autorisez la position pour un pointage précis
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={exportCsv} className="rounded-sm" data-testid="export-pointages-csv">
              <Download size={14} className="mr-1" /> CSV
            </Button>
            {isManager && (
              <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingTime(null); }}>
                <DialogTrigger asChild>
                  <Button onClick={openManualEntry} variant="outline" className="rounded-sm" data-testid="new-time-btn">
                    <Plus size={14} className="mr-1" /> Saisie manuelle
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex flex-col max-h-[90vh] p-0">
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                    <DialogTitle>{editingTime ? "Modifier le pointage" : "Saisie de pointage"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1" data-testid="time-dialog-scroll">
                    {isManager && (
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Collaborateur</Label>
                        <Select value={form.collaborateur_id} onValueChange={(v) => setForm({ ...form, collaborateur_id: v })}>
                          <SelectTrigger data-testid="time-collab"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Département</Label>
                      <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                        <SelectTrigger data-testid="time-brand"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                        <SelectContent>
                          {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Date</Label>
                        <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                      </div>
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Entrée</Label>
                        <TimePicker24 value={form.clock_in} onChange={(v) => setForm({ ...form, clock_in: v })} data-testid="time-clock-in" />
                      </div>
                      <div>
                        <Label className="label-uppercase mb-1.5 block">Sortie</Label>
                        <TimePicker24 value={form.clock_out} onChange={(v) => setForm({ ...form, clock_out: v })} data-testid="time-clock-out" />
                      </div>
                    </div>
                    <p className="text-[11px] text-zinc-500 font-mono">
                      <MapPin size={10} className="inline mr-1" />
                      {editingTime
                        ? "Édition admin — la géo-localisation initiale est conservée."
                        : "La position et le fuseau horaire actuels seront enregistrés à l'envoi."}
                    </p>
                  </div>
                  <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                    <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="time-save-btn">
                      {busy ? "Localisation…" : (editingTime ? "Mettre à jour" : "Enregistrer")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          {isManager && (
            openEntry ? (
              <Button onClick={quickClockOut} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm w-full sm:w-auto" data-testid="clock-out-btn">
                <LogOut size={14} className="mr-1" /> {busy ? "Localisation…" : "Pointer la sortie"}
              </Button>
            ) : (
              <Button onClick={quickClockIn} disabled={busy} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm w-full sm:w-auto" data-testid="clock-in-btn">
                <LogIn size={14} className="mr-1" /> {busy ? "Localisation…" : "Pointer l'entrée"}
              </Button>
            )
          )}
        </div>
      </div>

      {/* --- Sélecteur d'onglets (modèle EquipmentRequestsTab.jsx) --------- */}
      <div
        className="bg-white border border-zinc-200 rounded-sm p-2 inline-flex gap-1 flex-wrap"
        role="tablist"
        aria-label="Statut du pointage"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              data-testid={`pointage-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border rounded-sm transition ${active ? t.activeCls : `${t.cls} hover:brightness-95`}`}
            >
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>
      </StickyPageHeader>

      {/* --- Contenu de l'onglet sélectionné ------------------------------- */}
      {tab === "retard" && (
        <PointageRetardTab brands={brands} />
      )}
      {tab === "encours" && (
        <PointageEnCoursTab
          brands={brands}
          users={users}
          sites={sites}
          isManager={isManager}
          isAdmin={isAdmin}
          onEdit={openEditTime}
          onDelete={deleteTime}
          refreshSignal={refreshSignal}
        />
      )}
      {tab === "termine" && (
        <PointageTermineTab
          brands={brands}
          users={users}
          sites={sites}
          isAdmin={isAdmin}
          onEdit={openEditTime}
          onDelete={deleteTime}
          refreshSignal={refreshSignal}
        />
      )}
    </div>
  );
}
